import cn from 'classnames';
import React, { useCallback } from 'react';
import { MMNavItem as INavItem } from '.';
import Icon from 'tbk-components/src/components/Icon';

export interface IMMNavItemProps {
	item: INavItem;
	dropdownEnabled?: boolean;
	dropdownAutoClose?: 'true' | 'false' | 'inside' | 'outside';
	collapseEnabled?: boolean;
	dropdownPlacement?: 'dropstart' | 'dropend' | 'dropup';
	level?: number;
}

const NavItem: React.FC<IMMNavItemProps> = ({
	item,
	dropdownEnabled = true,
	dropdownAutoClose,
	collapseEnabled = false,
	dropdownPlacement,
	level = 0,
}) => {
	let linkArgs: any = {};
	const isDropdown =
		item.children &&
		!(Array.isArray(item.children) && item.children.length < 1);

	const isMegaMenu = isDropdown && !Array.isArray(item.children);

	if (item.isActive === true) {
		linkArgs['aria-current'] = 'page';
	}

	if (item.isDisabled === true) {
		linkArgs['aria-disabled'] = 'true';
		linkArgs['tabIndex'] = '-1';
	}

	if (item.target) {
		linkArgs['target'] = item.target;
	}

	const maybeDropdown = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.stopPropagation(); // this is just to prevent the click from closing the parent dropdown
		},
		[],
	);

	if (isDropdown) {
		linkArgs['aria-expanded'] = 'false';
		linkArgs['role'] = 'button';

		if (dropdownEnabled) {
			linkArgs['data-bs-toggle'] = 'dropdown';
			if (!dropdownAutoClose) {
				linkArgs['data-bs-auto-close'] = isMegaMenu
					? 'outside'
					: 'true';
			}
		}

		if (collapseEnabled) {
			linkArgs['data-bs-toggle'] = 'collapse';
		}

		if (level > 0) {
			linkArgs['onClick'] = maybeDropdown;
		}
	} else {
		if (item.target || item.target !== '_self') {
			linkArgs['rel'] = 'noopener';
		}
	}

	return item?.children || item?.href ? (
		<li
			className={cn(
				'nav-item',
				isDropdown &&
					dropdownEnabled && [
						isDropdown && 'dropdown',
						level > 0 && 'submenu',
						dropdownPlacement,
					],
				isMegaMenu && 'dropdown-mega-menu',
				...(item.classNames || []),
			)}
		>
			{item.icon && (
				<div className="nav-item-icon">
					<Icon name={item.icon} />
				</div>
			)}
			{(item?.href || item?.label) && (
				<a
					className={cn(
						'nav-link',
						item.isActive === true && 'active',
						item.isDisabled === true && 'disabled',
						dropdownEnabled &&
							isDropdown === true &&
							'dropdown-toggle',
						collapseEnabled &&
							isDropdown === true &&
							'collapse-toggle',
					)}
					href={item.href}
					{...linkArgs}
				>
					{item.label}
				</a>
			)}
			{item.description && (
				<div className="nav-item-description">{item.description}</div>
			)}
			{isDropdown && (
				<ul
					className={cn(
						dropdownEnabled && 'dropdown-menu',
						collapseEnabled && 'collapse',
						isMegaMenu && 'mega-menu',
					)}
				>
					{isMegaMenu === true ? (
						// @ts-ignore
						<li>{item.children}</li>
					) : (
						(item.children! as INavItem[]).map(
							(item, childIndex) => (
								<NavItem
									key={childIndex}
									item={item}
									dropdownEnabled={dropdownEnabled}
									dropdownAutoClose={dropdownAutoClose}
									collapseEnabled={collapseEnabled}
									dropdownPlacement={
										dropdownPlacement || 'dropend'
									}
									level={level + 1}
								/>
							),
						)
					)}
				</ul>
			)}
		</li>
	) : null;
};

export default NavItem;
